import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Search, ShieldCheck, Ban, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      const { data } = await adminAPI.getUsers(params);
      setUsers(data.users); setTotal(data.total); setPages(data.pages);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, [page]);

  const handleBan = async (userId) => {
    try {
      const { data } = await adminAPI.banUser(userId);
      toast.success(data.banned ? 'User banned' : 'User unbanned');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleVerify = async (userId) => {
    try {
      const { data } = await adminAPI.verifyUser(userId);
      toast.success(data.verified ? 'User verified' : 'Verification removed');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Permanently delete this user and all their listings?')) return;
    try {
      await adminAPI.deleteUser(userId);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="admin-users">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">User Management</h1>
        <p className="text-sm text-zinc-500 mt-1">{total} total users</p>
      </div>

      <div className="mb-4">
        <form onSubmit={e => { e.preventDefault(); setPage(1); fetchUsers(); }} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or ID..."
              className="w-full pl-10 pr-4 py-2.5 border border-zinc-200 rounded-lg text-sm focus:ring-2 focus:ring-[#002FA7] bg-white"
              data-testid="admin-users-search"
            />
          </div>
          <button type="submit" className="bg-[#002FA7] text-white px-4 py-2.5 rounded-lg text-sm font-bold" data-testid="admin-users-search-btn">Search</button>
        </form>
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">User</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Email</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">City</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Listings</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Joined</th>
                <th className="text-right px-4 py-3 font-semibold text-zinc-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-zinc-100"><td colSpan={7} className="px-4 py-3"><div className="h-4 bg-zinc-100 rounded animate-pulse" /></td></tr>
                ))
              ) : users.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-400">No users found</td></tr>
              ) : users.map(u => (
                <tr key={u.user_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-500 flex-shrink-0 overflow-hidden">
                        {u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : u.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold text-zinc-900">{u.name}</p>
                        <p className="text-xs text-zinc-400">{u.user_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{u.email}</td>
                  <td className="px-4 py-3 text-zinc-600">{u.city}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {u.role === 'admin' && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#002FA7] text-white">Admin</span>}
                      {u.verified && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">Verified</span>}
                      {u.banned && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">Banned</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{u.listings_count || 0}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    {u.role !== 'admin' && (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleVerify(u.user_id)} title={u.verified ? 'Remove verification' : 'Verify user'}
                          className={`p-1.5 rounded-md transition-colors ${u.verified ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100' : 'text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50'}`}
                          data-testid={`verify-user-${u.user_id}`}
                        ><ShieldCheck className="w-4 h-4" /></button>
                        <button onClick={() => handleBan(u.user_id)} title={u.banned ? 'Unban user' : 'Ban user'}
                          className={`p-1.5 rounded-md transition-colors ${u.banned ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-zinc-400 hover:text-red-600 hover:bg-red-50'}`}
                          data-testid={`ban-user-${u.user_id}`}
                        ><Ban className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(u.user_id)} title="Delete user"
                          className="p-1.5 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          data-testid={`delete-user-${u.user_id}`}
                        ><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-200 bg-zinc-50">
            <p className="text-xs text-zinc-500">Page {page} of {pages}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-1.5 rounded-md border border-zinc-200 disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="p-1.5 rounded-md border border-zinc-200 disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
